/**
 * Cross-module X XWP-15 (refonte WP-40; design/ux/04_produits_stock_assistant.md §2) — every French
 * string of Stock & lots, « Corriger le stock », « Corrections de stock » and « Risque de péremption ».
 * Never a literal in a component (one place to review the copy).
 */
import { fmtDate, fmtInt, fmtMoney } from "./locale.config.js";

const pluriel = (n, un, plusieurs) => (Math.abs(Number(n)) > 1 ? plusieurs : un);
const signe = (n) => (Number(n) > 0 ? `+${fmtInt(n)}` : Number(n) < 0 ? `−${fmtInt(-Number(n))}` : "0");

export const STOCK_LOTS = Object.freeze({
  titre: "Stock & lots",
  grilleLots: "Lots en stock",
  grilleProduits: "Produits en alerte",
  vues: "Vues",
  vuesLabel: "Vues du stock",
  corrections: "Corrections de stock",
  // X XWP-16 — back to the magasins workspace on the filtered magasin (XMD-01-23 « and back »)
  voirMagasin: "Voir dans Magasins",
  exporter: "Exporter",
  exporterLots: "CSV des lots (vue affichée)",
  exporterProduitsVue: "CSV des produits (vue affichée)",
  exporterProduits: "CSV du stock par produit (tous les produits)",
  imprimer: "Imprimer",
  recherche: "Rechercher : produit, DCI, labo, N° lot, code lot…",
  rechercheLabel: "Rechercher un lot ou un produit",
  inclureEpuises: "Inclure les lots épuisés",
  filtres: Object.freeze({
    type: "Type",
    laboratoire: "Laboratoire",
    marque: "Marque",
    magasin: "Magasin",
    peremptionAvant: "Péremption avant",
    produit: "Produit",
    // refonte WP-101 (CP §15.6, D-42) — the producer facet the selected types do not use, greyed
    laboratoireSansObjet: "Sans objet pour les types choisis : ils sont identifiés par leur marque.",
    marqueSansObjet: "Sans objet pour les types choisis : ils sont rattachés à un laboratoire.",
  }),
  tronque: "Liste limitée — affinez la recherche",
  colonnes: Object.freeze({
    produit: "Produit",
    lot: "N° lot",
    magasin: "Magasin",
    peremption: "Péremption",
    restant: "Restant",
    ppa: "PPA",
    etat: "État",
    laboratoire: "Laboratoire",
    // X XWP-97 (D-49) — beside « Laboratoire » (refonte WP-102's first part: « Stock initial », « Prix HT »)
    prixHt: "Prix HT",
    stockInitial: "Stock initial",
    vendable: "Stock vendable",
    seuil: "Seuil d'alerte",
    qteMin: "QTE MIN",
    enCommande: "En commande",
  }),
  codeLot: (c) => `Code lot ${c}`,
  sansCodeLot: "Sans code lot",
  lotNonRenseigne: "Lot non renseigné",
  sansPeremption: "Sans péremption",
  autresMagasins: (n) => `+ ${fmtInt(n)} ${pluriel(n, "autre magasin", "autres magasins")}`,
  dontUg: (n) => `dont ${fmtInt(n)} UG`,
  nonPlace: "—",
  nonEvalue: "Non évalué",
  nonRenseigne: "—",
  pagination: (debut, fin, total) => `${fmtInt(debut)}–${fmtInt(fin)} sur ${fmtInt(total)}`,
  page: (page, pages) => `Page ${fmtInt(page)} / ${fmtInt(pages)}`,
  pagePrecedente: "Précédent",
  pageSuivante: "Suivant",
  aide: "F2 : rechercher · Entrée : ouvrir · → : réceptions · Alt+C : corriger · Maj+F10 : actions · F1 : raccourcis",
  vide: (recherche, filtre) =>
    recherche
      ? `Aucun lot ne correspond à « ${recherche} »${filtre ? " avec les filtres actifs" : ""}.`
      : filtre
        ? "Aucun lot ne correspond aux filtres actifs."
        : "Aucun lot dans cette vue.",
  videProduits: "Aucun produit dans cette vue.",
  alerteFiltre: (n) => `Filtre : stock ≤ seuil d'alerte${n != null ? ` (${fmtInt(n)})` : ""}`,
  alerteExplication: "Produits dont le stock vendable est inférieur ou égal à leur seuil d'alerte, ruptures comprises.",
  ruptureExplication: "Produits sans aucune unité vendable (lots périmés, bloqués ou réservés exclus).",
  stockBasExplication: "Produits dont le stock vendable est entre 1 et leur seuil d'alerte.",
  retirerFiltre: "Retirer le filtre",
  actions: Object.freeze({
    ouvrir: "Ouvrir",
    corriger: "Corriger le stock",
    etiquette: "Imprimer l'étiquette",
    voirProduit: "Voir le produit",
  }),
  // X XWP-97 (XO-25) — identical receptions (same N° de lot, péremption, PPA) read as one row
  receptions: (n) => `${fmtInt(n)} ${pluriel(n, "réception", "réceptions")}`,
  receptionsAide: "Même N° de lot, même péremption, même PPA : une ligne, chaque facture gardée dessous (→ pour déplier).",
  receptionsTitre: (n) => `${fmtInt(n)} réceptions de ce lot`,
  receptionsColonnes: Object.freeze({
    facture: "Facture",
    recue: "Reçue le",
    puHt: "PU HT",
    quantite: "Restant / reçu",
    blocage: "Bloqué",
    visibilite: "Caissiers",
  }),
  receptionSansFacture: "Sans facture d'achat",
  receptionFacture: (f) =>
    [f?.numero_facture_fournisseur ? `Facture ${f.numero_facture_fournisseur}` : f?.numero_interne != null ? `Facture n° ${fmtInt(f.numero_interne)}` : null, f?.fournisseur?.nom ?? null]
      .filter(Boolean)
      .join(" · "),
  receptionRecue: (d) => (d ? fmtDate(d) : "—"),
  receptionQuantite: (reste, recu) => `${fmtInt(reste)} / ${fmtInt(recu)}`,
  receptionBloque: (n) => (Number(n) > 0 ? fmtInt(n) : "—"),
  visibleCaissiers: "Visible",
  prixPlage: (min, max) => `${fmtMoney(min)} – ${fmtMoney(max)}`,
  // X XWP-97 (D-49, XO-23 (5)) — the lot hidden from the caissiers at the counter (P / M see it)
  invisible: "Invisible",
  invisiblePartiel: (n, total) => `Invisible (${fmtInt(n)}/${fmtInt(total)})`,
  invisibleAide: "Invisible aux caissiers : ce lot n'est ni proposé ni vendable au comptoir.",
  correctionFaite: (delta, numero) =>
    `Stock corrigé (${signe(delta)} ${pluriel(delta, "unité", "unités")})${numero ? ` — lot ${numero}` : ""}`,
  exportImpossible: "Export impossible",
});

/** The État chip of a lot row (server `etat`, ux/04 §2.1.2) → its tone. */
export const ETATS_LOT_UI = Object.freeze({
  VENDABLE: Object.freeze({ libelle: "Vendable", ton: "success" }),
  PER_90: Object.freeze({ libelle: null, ton: "warning" }), // label from the server thresholds
  PER_30: Object.freeze({ libelle: null, ton: "danger" }),
  PERIME: Object.freeze({ libelle: "Périmé", ton: "danger" }),
  EPUISE: Object.freeze({ libelle: "Épuisé", ton: "neutral" }),
  BLOQUE: Object.freeze({ libelle: "Bloqué", ton: "warning" }),
  RESERVE: Object.freeze({ libelle: "Réservé en caisse", ton: "info" }),
  NON_DISPONIBLE: Object.freeze({ libelle: "Non disponible", ton: "neutral" }),
});

/** The État of a product row of the alert views (server `niveau`). */
export const NIVEAUX_ALERTE_UI = Object.freeze({
  RUPTURE: Object.freeze({ libelle: "Rupture", ton: "danger" }),
  BAS: Object.freeze({ libelle: "Stock bas", ton: "warning" }),
});

export const CORRIGER_STOCK = Object.freeze({
  titre: "Corriger le stock",
  sousTitre: ({ produit, numero, peremption, code }) =>
    [produit, numero ? `Lot ${numero}` : null, peremption, code ? `Code lot ${code}` : null].filter(Boolean).join(" · "),
  chargement: "Lecture du lot…",
  erreurChargement: "Impossible de lire ce lot.",
  reessayer: "Réessayer",
  magasin: "Magasin concerné",
  magasinOption: (nom, q) => `${nom} (${fmtInt(q)})`,
  magasinAide: "Le lot est réparti sur plusieurs magasins : comptez celui-ci uniquement.",
  systeme: "Quantité en stock (système)",
  compte: "Quantité comptée",
  compteAide: "Le nombre d'unités réellement présentes",
  ecart: "Écart",
  ugApres: (avant, apres) => `UG restantes après : ${fmtInt(avant)} → ${fmtInt(apres)}`,
  retenu: (n) => `${fmtInt(n)} ${pluriel(n, "unité bloquée ou réservée", "unités bloquées ou réservées")} : la quantité comptée ne peut pas être inférieure.`,
  motif: "Motif",
  precision: "Précision",
  precisionAide: "Obligatoire si « Autre »",
  motDePasse: "Votre mot de passe",
  motDePasseAide: "Votre mot de passe de connexion confirme la correction.",
  valider: "Valider la correction",
  annuler: "Annuler",
  pied: "Entrée / F9 : valider · Échap : annuler",
  erreurs: Object.freeze({
    compte: "Saisissez la quantité comptée (nombre entier, 0 ou plus).",
    compteMax: (max) => `Quantité maximale : ${fmtInt(max)}.`,
    sousRetenu: (n) => `Au moins ${fmtInt(n)} (unités bloquées ou réservées en caisse).`,
    aucunEcart: "Aucun écart : la quantité comptée est celle du système.",
    magasin: "Choisissez le magasin compté.",
    motif: "Choisissez un motif.",
    precision: "Précisez le motif (au moins 3 caractères).",
    motDePasse: "Votre mot de passe est requis.",
    motDePasseFaux: "Mot de passe incorrect.",
  }),
  recharger: "Recharger le lot",
  erreur: "La correction n'a pas pu être enregistrée.",
});

export const JOURNAL_CORRECTIONS_UI = Object.freeze({
  titre: "Corrections de stock",
  sousTitre: (total) => `${fmtInt(total)} ${pluriel(total, "correction", "corrections")}`,
  grille: "Journal des corrections de stock",
  recherche: "Rechercher : produit, N° lot, code lot…",
  rechercheLabel: "Rechercher une correction",
  filtres: Object.freeze({ periode: "Période", utilisateur: "Par", motif: "Motif", magasin: "Magasin" }),
  colonnes: Object.freeze({
    date: "Date",
    produit: "Produit",
    lot: "Lot",
    magasin: "Magasin",
    avant: "Avant",
    apres: "Après",
    ecart: "Écart",
    motif: "Motif",
    par: "Par",
  }),
  ecart: signe,
  motifLibre: "Motif libre",
  vide: (filtre) => (filtre ? "Aucune correction ne correspond aux filtres actifs." : "Aucune correction de stock enregistrée."),
  ouvrirStock: "Corriger depuis Stock & lots",
  aide: "F2 : rechercher · Entrée : ouvrir le produit · F1 : raccourcis",
});

export const RISQUE_PEREMPTION_UI = Object.freeze({
  titre: "Risque de péremption",
  sousTitre: "Ce qui ne sera pas vendu avant de périmer",
  classement: "Classement : valeur à risque décroissante (quantité non écoulée avant péremption × PU HT)",
  classementSansValeur: "Classement : quantité à risque décroissante",
  grille: "Lots à risque de péremption",
  horizon: "Horizon",
  rotation: "Période de rotation",
  magasin: "Magasin",
  tous: "Tous",
  jours: (n) => `${fmtInt(n)} j`,
  colonnes: Object.freeze({
    produit: "Produit",
    lot: "N° lot",
    peremption: "Péremption",
    restant: "Restant",
    rotation: "Rot./j",
    ecoulable: "Écoulable",
    aRisque: "À risque",
    puHt: "PU HT",
    valeur: "Valeur risque",
  }),
  formule:
    "Écoulable = unités vendues avant la péremption au rythme de la rotation, les lots du produit sortant dans l'ordre FEFO · À risque = Restant − Écoulable · Valeur risque : UG à 0.",
  totaux: ({ lots, unites, valeur }) =>
    [
      `${fmtInt(lots)} ${pluriel(lots, "lot", "lots")} à risque`,
      `${fmtInt(unites)} ${pluriel(unites, "unité", "unités")}`,
      valeur != null ? `${valeur} à risque` : null,
    ]
      .filter(Boolean)
      .join(" · "),
  perime: "Périmé",
  dans: (j) => (Number(j) === 0 ? "aujourd'hui" : `dans ${fmtInt(j)} j`),
  vide: "Aucun lot à risque sur cet horizon : tout sera vendu avant de périmer.",
  exporter: "Exporter",
  exporterCsv: "CSV du classement",
  imprimer: "Imprimer",
  aide: "Entrée : ouvrir le produit · Alt+X : exporter · F1 : raccourcis",
});

/**
 * Refonte WP-130 (D-51; brief 2026-10-26 §7) — « Mouvements de stock » of the fiche stock & lots: the
 * chart (blue stock, red ventes, green entrées), its tooltip / keyboard detail and its table.
 */
export const MOUVEMENTS_STOCK = Object.freeze({
  onglet: "Mouvements de stock",
  ongletLots: "Lots",
  ongletsLabel: "Fiche stock & lots",
  titre: "Mouvements de stock",
  ligne: "Lot affiché",
  tousLesLots: "Tous les lots du produit",
  optionLigne: (numero, peremption, receptions) =>
    `${numero || "Sans N° de lot"} · ${peremption}${receptions > 1 ? ` · ${receptions} réceptions` : ""}`,
  voirMouvements: "Mouvements",
  voirMouvementsLabel: (numero) => `Mouvements de stock du lot ${numero || "sans N°"}`,
  series: Object.freeze({
    stock: "Quantité en stock",
    ventes: "Ventes",
    entrees: "Entrées de stock",
  }),
  legende: "Légende",
  chargement: "Chargement des mouvements…",
  erreur: "Impossible de charger les mouvements de stock",
  aucun: "Aucun mouvement enregistré sur la période.",
  navigation: "Graphique : ← / → change de jour, ↑ / ↓ change de couleur (stock, ventes, entrées). Survolez ou choisissez une barre pour voir son détail.",
  navigationSemaine: "Graphique : ← / → change de semaine, ↑ / ↓ change de couleur (stock, ventes, entrées). Survolez ou choisissez une barre pour voir son détail.",
  graphique: "graphique en barres",
  parSemaine: "Une barre par semaine au-delà de 90 jours.",
  journalDepuis: (jour) => `Mouvements enregistrés à partir du ${fmtDate(jour)} : avant cette date, le stock est montré constant.`,
  resume: (t, stock) =>
    `Sur la période : ${fmtInt(t.ventes)} vendue${t.ventes > 1 ? "s" : ""} · ${fmtInt(t.entrees)} entrée${t.entrees > 1 ? "s" : ""}` +
    `${t.autres_entrees || t.autres_sorties ? ` · autres mouvements +${fmtInt(t.autres_entrees)} / −${fmtInt(t.autres_sorties)}` : ""} · stock ${fmtInt(stock.debut_periode)} → ${fmtInt(stock.fin_periode)}`,
  jour: (b) => (b.debut === b.fin ? fmtDate(b.debut) : `${fmtDate(b.debut)} – ${fmtDate(b.fin)}`),
  etiquetteJour: (jour) => `${jour.slice(8, 10)}/${jour.slice(5, 7)}`,
  stockFin: (b, semaine) => `Stock en fin de ${semaine ? "semaine" : "journée"} : ${fmtInt(b.stock_fin)}`,
  titreVentes: (b) => `Ventes · ${fmtInt(b.ventes)} unité${b.ventes > 1 ? "s" : ""}`,
  titreEntrees: (b) => `Entrées de stock · ${fmtInt(b.entrees)} unité${b.entrees > 1 ? "s" : ""}`,
  aucuneVente: "Aucune vente ce jour-là.",
  aucuneEntree: "Aucune entrée ce jour-là.",
  vente: Object.freeze({
    vendeur: "Vendeur",
    type: "Type",
    ticket: "N° de ticket",
    totalTicket: "Total du ticket",
    quantite: "Quantité",
    lignes: (n) => `${fmtInt(n)} ligne${n > 1 ? "s" : ""}`,
    annulee: "Annulée",
    inconnue: "Vente non retrouvée",
  }),
  entree: Object.freeze({
    facture: "Facture d'achat",
    piece: "Pièce",
    fournisseur: "Fournisseur",
    date: "Date",
    puHt: "PU HT",
    quantite: "Quantité",
    ug: (n) => ` (+${fmtInt(n)} UG)`,
    consulter: "Consulter",
    consulterLabel: (numero) => `Consulter la pièce ${numero ?? ""} dans une nouvelle fenêtre`.trim(),
    sansPiece: "Pièce non retrouvée",
  }),
  autres: (b) => `Autres mouvements : +${fmtInt(b.autres_entrees)} / −${fmtInt(b.autres_sorties)}`,
  tronques: (n) => `… et ${fmtInt(n)} autre${n > 1 ? "s" : ""} (période plus courte pour tout voir)`,
  lot: (numero) => `Lot ${numero || "sans N°"}`,
  montant: (v) => fmtMoney(v),
  tableau: Object.freeze({
    titre: "Détail par jour",
    titreSemaine: "Détail par semaine",
    jour: "Jour",
    semaine: "Semaine",
    stock: "Stock fin",
    ventes: "Ventes",
    entrees: "Entrées",
    autres: "Autres",
    vide: "Aucun jour avec des ventes ou des entrées.",
    voir: (serie, b) => `Voir le détail : ${serie} du ${b.debut === b.fin ? fmtDate(b.debut) : `${fmtDate(b.debut)} au ${fmtDate(b.fin)}`}`,
  }),
});
